import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiUser, FiArrowRight, FiTool, FiSettings, FiShield, FiTrendingUp } = FiIcons;

const BlogPage = () => {
  const blogPosts = [
    {
      id: 1,
      title: 'Precision Machining Tolerances: Industry Standards vs Practical Applications',
      excerpt: 'Understanding the balance between theoretical tolerances and real-world manufacturing capabilities in precision machining operations.',
      author: 'Engineering Team',
      date: '2024-01-15',
      category: 'Precision Machining',
      icon: FiTool,
      readTime: '8 min read'
    },
    {
      id: 2,
      title: 'Materials Selection for South African Operating Conditions',
      excerpt: 'A comprehensive guide to selecting appropriate materials that can withstand the unique environmental challenges of South African industrial operations.',
      author: 'Materials Engineer',
      date: '2024-01-10',
      category: 'Materials Science',
      icon: FiSettings,
      readTime: '12 min read'
    },
    {
      id: 3,
      title: 'Quality Assurance in High-Volume Manufacturing',
      excerpt: 'Implementing effective quality control systems that maintain consistency across large production runs while optimizing efficiency.',
      author: 'Quality Manager',
      date: '2024-01-05',
      category: 'Quality Control',
      icon: FiShield,
      readTime: '10 min read'
    },
    {
      id: 4,
      title: 'Safety Innovations in Heavy Engineering Projects',
      excerpt: 'Latest safety technologies and methodologies being implemented in large-scale industrial fabrication projects.',
      author: 'Safety Director',
      date: '2023-12-28',
      category: 'Safety',
      icon: FiShield,
      readTime: '6 min read'
    },
    {
      id: 5,
      title: 'CNC Technology Advances and Manufacturing Efficiency',
      excerpt: 'How modern CNC technology is revolutionizing manufacturing efficiency and enabling new possibilities in component production.',
      author: 'Production Manager',
      date: '2023-12-20',
      category: 'Technology',
      icon: FiTrendingUp,
      readTime: '9 min read'
    },
    {
      id: 6,
      title: 'Sustainable Manufacturing Practices and Waste Reduction',
      excerpt: 'Implementing environmentally responsible manufacturing processes while maintaining productivity and quality standards.',
      author: 'Environmental Officer',
      date: '2023-12-15',
      category: 'Sustainability',
      icon: FiSettings,
      readTime: '7 min read'
    }
  ];

  const categories = [
    'All Posts',
    'Precision Machining',
    'Materials Science',
    'Quality Control',
    'Safety',
    'Technology',
    'Sustainability'
  ];

  const [selectedCategory, setSelectedCategory] = React.useState('All Posts');

  const filteredPosts = selectedCategory === 'All Posts' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  const featuredPost = blogPosts[0];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-900 to-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Industry Insights</h1>
            <p className="text-xl md:text-2xl max-w-4xl mx-auto text-gray-200">
              Expert knowledge and insights from four decades of manufacturing excellence. 
              Stay informed about the latest trends, technologies, and best practices in industrial manufacturing.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Post */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Featured Article</h2>
            <div className="bg-gradient-to-r from-blue-50 to-gray-50 rounded-2xl p-8 lg:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <SafeIcon icon={featuredPost.icon} className="text-xl text-blue-800" />
                    </div>
                    <span className="bg-blue-800 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {featuredPost.category}
                    </span>
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">{featuredPost.title}</h3>
                  <p className="text-lg text-gray-600 mb-6">{featuredPost.excerpt}</p>
                  <div className="flex items-center space-x-6 text-sm text-gray-500 mb-6">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiUser} />
                      <span>{featuredPost.author}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiCalendar} />
                      <span>{new Date(featuredPost.date).toLocaleDateString()}</span>
                    </div>
                    <span>{featuredPost.readTime}</span>
                  </div>
                  <button className="bg-blue-800 hover:bg-blue-900 text-white px-6 py-3 rounded-lg font-semibold transition-colors inline-flex items-center space-x-2">
                    <span>Read Full Article</span>
                    <SafeIcon icon={FiArrowRight} />
                  </button>
                </div>
                <div className="relative">
                  <div className="bg-gradient-to-br from-blue-100 to-orange-100 rounded-xl p-8 h-80 flex items-center justify-center">
                    <SafeIcon icon={FiTool} className="text-8xl text-blue-800" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Filter */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Latest Articles</h2>
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-blue-800 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Blog Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.slice(1).map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <SafeIcon icon={post.icon} className="text-lg text-blue-800" />
                    </div>
                    <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-medium">
                      {post.category}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                    {post.title}
                  </h3>
                  
                  <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiUser} className="text-xs" />
                        <span>{post.author}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <SafeIcon icon={FiCalendar} className="text-xs" />
                        <span>{new Date(post.date).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <span className="text-xs">{post.readTime}</span>
                  </div>
                  
                  <button className="text-blue-800 hover:text-blue-900 font-semibold inline-flex items-center space-x-2 transition-colors">
                    <span>Read More</span>
                    <SafeIcon icon={FiArrowRight} className="text-sm" />
                  </button>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-20 bg-gradient-to-r from-blue-800 to-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="text-4xl font-bold mb-6">Stay Updated</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Subscribe to our newsletter for the latest insights, industry trends, 
              and manufacturing innovations delivered to your inbox.
            </p>
            <div className="flex flex-col sm:flex-row max-w-md mx-auto gap-4">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-4 py-3 rounded-lg text-gray-900 focus:ring-2 focus:ring-orange-500 focus:outline-none"
              />
              <button className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors">
                Subscribe
              </button>
            </div>
            <p className="text-sm text-gray-300 mt-4">
              No spam, unsubscribe at any time. We respect your privacy.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;